import React from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Check, XCircle, Info } from '@phosphor-icons/react';

// Two live regions are mounted permanently (not on-demand) so screen readers
// have already registered them before any message is inserted — creating a
// live region at the same moment you populate it is the classic mistake that
// makes many screen readers announce nothing.
//
// Politeness is chosen per toast type, not global: "error" toasts use
// role="alert"/aria-live="assertive" because something went wrong and the
// user needs to know now, even if it interrupts. "success"/"info" toasts use
// role="status"/aria-live="polite" because they're routine confirmations that
// should wait for a pause rather than cut off whatever the user is doing —
// assertive is easy to overuse and would make every save/delete feel like an
// emergency. Content always lands in the region matching the active toast's
// urgency; the other stays empty.
export default function Toast({ toast }) {
  const isError = toast?.type === 'error';

  return (
    <>
      <div role="status" aria-live="polite" aria-atomic="true">
        <AnimatePresence>
          {toast && !isError && <ToastBubble toast={toast} />}
        </AnimatePresence>
      </div>
      <div role="alert" aria-live="assertive" aria-atomic="true">
        <AnimatePresence>
          {toast && isError && <ToastBubble toast={toast} />}
        </AnimatePresence>
      </div>
    </>
  );
}

function ToastBubble({ toast }) {
  return (
    <motion.div
      className="toast-container"
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 20, scale: 0.95 }}
    >
      <div className={`toast toast-${toast.type}`}>
        {toast.type === 'success' && <SuccessIcon />}
        {toast.type === 'error' && <ErrorIcon />}
        {toast.type === 'info' && <InfoIcon />}
        {toast.message}
      </div>
    </motion.div>
  );
}

function SuccessIcon() {
  return <Check size={18} />;
}

function ErrorIcon() {
  return <XCircle size={18} />;
}

function InfoIcon() {
  return <Info size={18} />;
}
